import { useCheckBotsScroll } from "@hooks:useCheckBotsScroll";
import { Box } from "@mui/material";
import { Message } from "@pages:app/talk/Chat/components/message-view/Message";
import { ChatMessage } from "@pages:app/talk/Chat/components/utils/message";
import { throttle } from "lodash";
import { useCallback, useEffect, useRef } from "react";

interface MessageViewProps {
  messagesTree: ChatMessage[];
}

export const MessageView = ({ messagesTree }: MessageViewProps) => {
  const messageEnd = useRef<HTMLDivElement>(null);
  const lastMessageKey = useRef<string | undefined>(
    messagesTree.length ? messagesTree[messagesTree.length - 1].key : undefined,
  );
  const { isScrollEnabled } = useCheckBotsScroll();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scrollToRef = useCallback(
    throttle(
      () => messageEnd.current?.scrollIntoView({ behavior: "smooth" }),
      500,
      { leading: true },
    ),
    [messageEnd],
  );

  useEffect(() => {
    if (!messagesTree) {
      return;
    }

    // Bot streaming only updates the last message's text, not its key, so it stays gated behind isScrollEnabled.
    // User sends/retries and conversation switches change the last message's key, so we always scroll for those.
    const currentLastKey = messagesTree.length
      ? messagesTree[messagesTree.length - 1].key
      : undefined;
    const messageKeyChanged = currentLastKey !== lastMessageKey.current;
    lastMessageKey.current = currentLastKey;

    if (messageKeyChanged || isScrollEnabled) {
      scrollToRef();
    }
  }, [messagesTree, scrollToRef, isScrollEnabled]);

  useEffect(() => {
    messageEnd?.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (scrollToRef) {
      scrollToRef();
    }
  }, [scrollToRef]);

  return (
    <Box
      sx={{
        flex: "1 1 0%",
        overflow: "hidden",
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: "100%",
        }}
      >
        {messagesTree.map((message) => (
          <Message message={message} key={message.key} />
        ))}
        <Box
          ref={messageEnd}
          sx={{
            paddingY: "2rem",
          }}
        />
      </Box>
    </Box>
  );
};
